import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { removeNotification } from "../../redux/slices/uiSlice";

export const ToastContainer: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { notifications } = useSelector((state: RootState) => state.ui);

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-none">
      {notifications.map((notification: any) => (
        <Toast
          key={notification.id}
          id={notification.id}
          type={notification.type}
          message={notification.message}
          onClose={(id) => dispatch(removeNotification(id))}
        />
      ))}
    </div>
  );
};

// Toast component with auto-dismiss
interface ToastProps {
  id: string;
  type: "success" | "error" | "warning" | "info";
  message: string;
  onClose: (id: string) => void;
  duration?: number;
}

const Toast: React.FC<ToastProps> = ({
  id,
  type,
  message,
  onClose,
  duration = 5000,
}) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(id);
    }, duration);

    return () => clearTimeout(timer);
  }, [id, onClose, duration]);

  const typeStyles = {
    success: {
      bg: "bg-green-500/10 border-green-500/50",
      icon: "✓",
      iconBg: "bg-green-500",
      text: "text-green-400",
    },
    error: {
      bg: "bg-red-500/10 border-red-500/50",
      icon: "✕",
      iconBg: "bg-red-500",
      text: "text-red-400",
    },
    warning: {
      bg: "bg-yellow-500/10 border-yellow-500/50",
      icon: "!",
      iconBg: "bg-yellow-500",
      text: "text-yellow-400",
    },
    info: {
      bg: "bg-blue-500/10 border-blue-500/50",
      icon: "i",
      iconBg: "bg-blue-500",
      text: "text-blue-400",
    },
  };

  const style = typeStyles[type];

  return (
    <div
      className={`
        pointer-events-auto
        flex items-start gap-3 
        p-4 rounded-lg border 
        ${style.bg}
        backdrop-blur-sm
        shadow-lg
        animate-slide-in-right
        transition-all duration-300
      `}
      role="alert"
    >
      {/* Icon */}
      <div
        className={`
          flex-shrink-0 w-6 h-6 rounded-full 
          ${style.iconBg} 
          flex items-center justify-center
        `}
      >
        <span className="text-white text-xs font-bold">{style.icon}</span>
      </div>

      {/* Message */}
      <p className={`flex-1 text-sm ${style.text}`}>{message}</p>

      {/* Close button */}
      <button
        onClick={() => onClose(id)}
        className={`
          flex-shrink-0 
          ${style.text} 
          hover:opacity-70 
          transition-opacity
        `}
        aria-label="Close notification"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
};
